/**
 * Shared Email Validation Utilities
 * 
 * Two-tier validation system:
 * 1. Client-side: Instant typo detection with Mailcheck.js
 * 2. Server-side: Deep validation via API (disposable, deliverability, etc.)
 * 
 * Usage:
 * - Use validateEmailFormat() for instant client-side validation
 * - Use validateEmailWithSuggestion() for typo detection on blur
 * - Use validateEmailDeep() for server-side validation (optional)
 */

/**
 * RFC 5322 compliant email regex
 */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Common email typos to detect and suggest corrections
 */
const COMMON_TYPOS: Record<string, string> = {
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmil.com": "gmail.com",
  "yahooo.com": "yahoo.com",
  "yaho.com": "yahoo.com",
  "hotmial.com": "hotmail.com",
  "hotmil.com": "hotmail.com",
  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
  "iclod.com": "icloud.com",
  "icoud.com": "icloud.com",
};

/**
 * Popular email domains for autocomplete/suggestions
 */
export const POPULAR_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "protonmail.com",
  "aol.com",
  "mail.com",
];

/**
 * Validation result interface
 */
export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  suggestion?: string;
}

/**
 * Deep validation result (from API)
 */
export interface EmailDeepValidationResult extends EmailValidationResult {
  disposable?: boolean;
  deliverable?: boolean;
  quality?: "good" | "risky" | "bad";
}

/**
 * Basic email format validation
 * Fast, synchronous check for client-side use
 */
export function validateEmailFormat(email: string): EmailValidationResult {
  const trimmed = email.trim();

  // Empty check
  if (trimmed.length === 0) {
    return {
      isValid: false,
      error: "Email is required.",
    };
  }

  // Length checks (RFC 5321)
  if (trimmed.length > 254) {
    return {
      isValid: false,
      error: "Email address is too long.",
    };
  }

  // Format validation
  if (!EMAIL_REGEX.test(trimmed)) {
    return {
      isValid: false,
      error: "Please enter a valid email address.",
    };
  }

  // Split and validate parts
  const parts = trimmed.split("@");
  if (parts.length !== 2) {
    return {
      isValid: false,
      error: "Email must contain exactly one @ symbol.",
    };
  }

  const [localPart, domain] = parts;

  // Local part length check (RFC 5321)
  if (localPart.length > 64) {
    return {
      isValid: false,
      error: "Email address is invalid.",
    };
  }

  // Domain validation
  if (domain.length === 0 || !domain.includes(".")) {
    return {
      isValid: false,
      error: "Email domain is invalid.",
    };
  }

  // Security: Block dangerous characters (SQL injection, XSS)
  if (/[;<>'"\\]/.test(trimmed)) {
    return {
      isValid: false,
      error: "Email contains invalid characters.",
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Validate email with typo detection and suggestions
 * Use this on blur events for better UX
 */
export function validateEmailWithSuggestion(email: string): EmailValidationResult {
  // First, validate format
  const formatResult = validateEmailFormat(email);
  if (!formatResult.isValid) {
    return formatResult;
  }

  const trimmed = email.trim();
  const [localPart, domain] = trimmed.split("@");
  const domainLower = domain.toLowerCase();

  // Check for common typos
  if (COMMON_TYPOS[domainLower]) {
    return {
      isValid: true,
      suggestion: `${localPart}@${COMMON_TYPOS[domainLower]}`,
    };
  }

  // If the domain is already a known good popular domain, don't suggest alternatives
  if (POPULAR_DOMAINS.includes(domainLower)) {
    return { isValid: true };
  }

  // Check for similar domains (Levenshtein distance = 1)
  for (const popularDomain of POPULAR_DOMAINS) {
    // Avoid suggesting shorter substrings like mail.com when gmail.com was typed
    if (
      getLevenshteinDistance(domainLower, popularDomain) === 1 &&
      !domainLower.endsWith(popularDomain) // extra guard
    ) {
      return {
        isValid: true,
        suggestion: `${localPart}@${popularDomain}`,
      };
    }
  }

  return {
    isValid: true,
  };
}

/**
 * Deep email validation via Emailable API (through Edge Function)
 * Use this for critical flows (payment, signup, etc.)
 * 
 * @param email - Email address to validate
 * @returns Promise with deep validation results
 */
export async function validateEmailDeep(
  email: string
): Promise<EmailDeepValidationResult> {
  // First, validate format
  const formatResult = validateEmailFormat(email);
  if (!formatResult.isValid) {
    return formatResult;
  }

  try {
    // Dynamic import to avoid circular dependencies
    const { supabase } = await import("@/integrations/supabase/client");
    
    const { data, error } = await supabase.functions.invoke("validate-email", {
      body: { email: email.trim() },
    });

    if (error) {
      // Fail gracefully - don't block user
      console.warn("Email validation API failed, allowing submission", error);
      return {
        isValid: true,
      };
    }

    // Emailable states: deliverable, undeliverable, risky, unknown
    const isDeliverable = data.state === "deliverable" || data.state === "unknown";

    return {
      isValid: isDeliverable && !data.disposable,
      disposable: data.disposable,
      deliverable: isDeliverable,
      quality: data.quality,
      error: data.disposable
        ? "Disposable email addresses are not allowed."
        : data.state === "undeliverable"
        ? "This email address appears to be invalid."
        : data.role
        ? "Role-based email addresses (info@, admin@) are not allowed."
        : undefined,
      suggestion: data.suggestion,
    };
  } catch (error) {
    // Fail gracefully - don't block user if API is down
    console.error("Email validation failed:", error);
    return {
      isValid: true,
    };
  }
}

/**
 * Calculate Levenshtein distance between two strings
 * Used for fuzzy domain matching
 */
function getLevenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Check if email domain is in a list of blocked domains
 * Useful for blocking disposable email services
 */
export function isBlockedDomain(email: string, blockedDomains: string[]): boolean {
  const domain = email.split("@")[1]?.toLowerCase();
  return blockedDomains.includes(domain);
}

/**
 * Extract domain from email address
 */
export function getEmailDomain(email: string): string | null {
  const parts = email.split("@");
  return parts.length === 2 ? parts[1].toLowerCase() : null;
}

/**
 * Sanitize email for safe storage/display
 * Removes potentially dangerous characters
 */
export function sanitizeEmail(email: string): string {
  return email
    .trim()
    .toLowerCase()
    .replace(/[;<>'"\\]/g, "");
}
